import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";

import {
  buildInteractionSnapshot,
  createInteractionEvent,
  getAnalyticsConfig,
  getTargetPayload,
  persistInteractionEvent,
  readInteractionEvents,
} from "../services/interactionAnalytics";

const VIEW_SCROLL_BUCKETS = [25, 50, 75, 90];

const getScrollDepth = () => {
  if (typeof document === "undefined") return 0;

  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const scrollHeight = document.documentElement.scrollHeight || 0;
  const viewportHeight = window.innerHeight || 0;
  const maxScroll = Math.max(scrollHeight - viewportHeight, 1);
  const depth = Math.round((scrollTop / maxScroll) * 100);

  return Math.min(100, Math.max(0, depth));
};

const isInteractiveTarget = (element) =>
  element &&
  typeof element.closest === "function" &&
  element.closest(
    "button, a, input, select, textarea, [role='button'], [data-analytics-id]",
  );

const getClickCoordinates = (event) => {
  const x = Number(event.clientX);
  const y = Number(event.clientY);

  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return { x_percent: null, y_percent: null };
  }

  const width = window.innerWidth || 1;
  const height = window.innerHeight || 1;

  return {
    x_percent: Math.min(1, Math.max(0, x / width)),
    y_percent: Math.min(1, Math.max(0, y / height)),
  };
};

export const useInteractionAnalytics = ({
  currentView,
  userRole,
  userId,
  isActive = true,
}) => {
  const [events, setEvents] = useState(() => readInteractionEvents());
  const initializedRef = useRef(false);
  const scrollBucketRef = useRef(new Set());
  const clickBufferRef = useRef([]);

  const config = useMemo(() => getAnalyticsConfig(), []);
  const analyticsEnabled = Boolean(config.key);
  const trackingEnabled = analyticsEnabled && isActive;

  useEffect(() => {
    if (
      !trackingEnabled ||
      typeof window === "undefined" ||
      initializedRef.current
    ) {
      return;
    }

    posthog.init(config.key, {
      api_host: config.host,
      autocapture: true,
      capture_pageview: false,
      persistence: "localStorage",
      loaded: (client) => {
        if (userId) {
          client.identify(String(userId), {
            role: userRole || "unknown",
          });
        }
      },
    });

    initializedRef.current = true;
  }, [config.host, config.key, trackingEnabled, userId, userRole]);

  useEffect(() => {
    if (!initializedRef.current) return;

    if (!trackingEnabled) {
      posthog.reset();
      return;
    }

    if (userId) {
      posthog.identify(String(userId), {
        role: userRole || "unknown",
      });
    } else {
      posthog.reset();
    }
  }, [trackingEnabled, userId, userRole]);

  const appendEvent = useCallback(
    (type, payload = {}, captureInPosthog = true) => {
      const event = createInteractionEvent(type, {
        role: userRole || "unknown",
        user_id: userId || "",
        ...payload,
      });

      const nextEvents = persistInteractionEvent(event);
      // Defer the React state update out of the current (possibly native,
      // capture-phase) event dispatch. Re-rendering synchronously while a
      // click is still propagating reverts controlled inputs like checkboxes
      // mid-toggle. localStorage already holds the event, so the analytics
      // snapshot stays accurate — it just refreshes on the next tick.
      setTimeout(() => setEvents(nextEvents), 0);

      if (initializedRef.current && trackingEnabled && captureInPosthog) {
        posthog.capture(type, {
          ...payload,
          role: userRole || "unknown",
          user_id: userId || "",
          current_view: currentView || "unknown",
        });
      }
    },
    [currentView, trackingEnabled, userId, userRole],
  );

  useEffect(() => {
    if (!trackingEnabled) return;

    appendEvent(
      "page_view",
      {
        page: currentView || "unknown",
        view: currentView || "unknown",
      },
      true,
    );

    if (initializedRef.current) {
      posthog.capture("$pageview", {
        current_view: currentView || "unknown",
        role: userRole || "unknown",
      });
    }

    scrollBucketRef.current = new Set();
  }, [appendEvent, currentView, trackingEnabled, userRole]);

  useEffect(() => {
    if (!trackingEnabled) return;

    const handleClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const interactive = target ? target.closest("button, a, input, select, textarea, [role='button'], [data-analytics-id]") : null;

      if (!interactive || !isInteractiveTarget(interactive)) return;

      const payload = {
        ...getTargetPayload(interactive, currentView),
        ...getClickCoordinates(event),
      };

      appendEvent("interaction_click", payload);

      const now = Date.now();
      const targetKey = payload.analytics_id || payload.target_label;
      clickBufferRef.current = clickBufferRef.current.filter(
        (item) => now - item.timestamp < 1800,
      );
      clickBufferRef.current.push({
        targetKey,
        timestamp: now,
        x: event.clientX,
        y: event.clientY,
      });

      const sameTargetClicks = clickBufferRef.current.filter(
        (item) => item.targetKey === targetKey,
      );

      if (sameTargetClicks.length >= 3) {
        appendEvent("stuck_interaction", {
          reason: "rage_click",
          target_label: payload.target_label,
          analytics_id: payload.analytics_id,
          view: currentView || "unknown",
        });
        clickBufferRef.current = [];
      }
    };

    const handleSubmit = (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;

      appendEvent("form_submit", {
        form_label:
          form.getAttribute("data-analytics-id") ||
          form.getAttribute("aria-label") ||
          form.id ||
          "form",
        view: currentView || "unknown",
      });
    };

    const handleInvalid = (event) => {
      const field = event.target instanceof Element ? event.target : null;
      if (!field) return;

      appendEvent("form_invalid", {
        field_label:
          field.getAttribute("data-analytics-id") ||
          field.getAttribute("aria-label") ||
          field.getAttribute("name") ||
          field.tagName.toLowerCase(),
        view: currentView || "unknown",
      });
    };

    const handleScroll = () => {
      const depth = getScrollDepth();
      const bucket = VIEW_SCROLL_BUCKETS.find((threshold) => depth >= threshold);

      if (!bucket || scrollBucketRef.current.has(bucket)) return;

      scrollBucketRef.current.add(bucket);
      appendEvent("scroll_depth", {
        depth,
        bucket,
        view: currentView || "unknown",
      });
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("submit", handleSubmit, true);
    document.addEventListener("invalid", handleInvalid, true);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("submit", handleSubmit, true);
      document.removeEventListener("invalid", handleInvalid, true);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [appendEvent, currentView, trackingEnabled]);

  const snapshot = useMemo(() => buildInteractionSnapshot(events), [events]);

  return {
    analyticsEnabled,
    analyticsStatus: analyticsEnabled
      ? trackingEnabled
        ? "PostHog activo"
        : "PostHog conectado, captura pausada"
      : "Faltan variables de entorno",
    snapshot,
    trackEvent: appendEvent,
  };
};
